import { api, type GeoInfo, type Metric, type Scores } from "./api.ts";
import { fmt, LEVEL_LABEL, metricPeriod, MONTHS_LONG, periodLabel, zipOrigin, zipOriginNote, pct, pctChange } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import { metricSelect } from "./widgets.ts";
import { driverChart, outlookPill, rangeStrip, scoreMeter } from "./gauge.ts";
import { splitSummary, summarize, type Tone } from "../shared/questions.ts";
import { confidenceText, contextText, forecastChangeText, forecastConfidence, forecastReading, riseLabel, type Confidence } from "../shared/context.ts";
import { underwrite, type UnderwriteInput } from "../shared/underwrite.ts";
import { BRAND } from "../shared/brand.ts";
import { areaTitle, hasScorecard, marketPath, scorecardPath } from "../shared/marketpath.ts";
import { fromOf, inPurchaseRateState, taxRuleNote, usesPurchaseRule } from "../shared/regions.ts";
import { account, dismissMeter, lockedTeaser, meter, openSignup } from "./account.ts";
import { CA_HEADING, CA_INSURANCE, caTiles } from "../shared/catiles.ts";

export interface PanelDeps {
  metrics: Metric[];
  getMetric(): Metric;
  setMetric(id: string): void;
  getFreq(): "monthly" | "yearly";
  setFreq(f: "monthly" | "yearly"): void;
  onClose(): void;
  onNavigate(geoId: string): void;
  /** add this area to the comparison (the main app; not the iOS panel) */
  onCompare?(geoId: string): void;
  /** My budget (the main app): the affordability line offers it */
  budget?: { has(): boolean; open(): void; show(): void };
  /** the free-account gate applies (the main app; not the iOS panel): web/account.ts */
  gated?: boolean;
  /** open one of California's data points on the map (the main app switches to the California view first); else setMetric */
  showCaMetric?(id: string): void;
}

type ScoreTab = "forecast" | "longterm";
type ChartRange = "5y" | "10y" | "all";

/** A city's badge and the note on where its numbers come from (cities are a hidden level for now). */
const cityBadge = (g: GeoInfo) => (g.zillow_city ? (g.zip_built ? "City · Zillow values, listings from its ZIPs" : "City · Zillow values, no listings data")
  : g.zip_built ? "City · market data from its ZIPs" : "City · Census data only");
const cityNote = (g: GeoInfo) => [
  g.zillow_city ? "Home values and rents are Zillow's own for the city." : null,
  g.zip_built
    ? `${g.zillow_city ? "Listings and sales" : "Prices, rents, listings and sales"} are averages of the city's ZIP codes, each counted by its homes inside the city (${Math.round((g.zip_fit ?? 0) * 100)}% of that data is about the city itself${g.zip_rough ? ": read them as rough" : ""}). Numbers built this way are marked "from its ZIPs".`
    : `Its ZIP codes lie mostly outside the city, so ${g.zillow_city ? "listings and sales" : "prices and listings"} aren't shown.`,
  "Census figures are the city's own."].filter(Boolean).join(" ");

/** The area detail panel: identity chips, score card (forecast range, score meters, driver contributions) and the metric history chart. */
export class DetailPanel {
  private destroyChart: () => void = () => {};
  private geo?: GeoInfo;
  private scores?: Scores;
  private us?: Promise<GeoInfo | null>; // national latest values, for "U.S.: x" comparisons in the summary
  private tab: ScoreTab = "forecast";
  private showFair = false;
  /** the history chart's time range (kept across re-renders and areas) */
  private range: ChartRange = "all";
  /** which "more" sections are open; kept while the panel re-renders (metric or frequency change) */
  private openMore = new Set<string>();
  private seq = 0;
  private chartSeq = 0; // bumps on every chart request, so a slower older response never draws over a newer one

  constructor(private readonly root: HTMLElement, private readonly d: PanelDeps) {}

  get open() {
    return !this.root.hidden;
  }

  close() {
    this.seq++; // an area still loading must not render into the closed panel or count a view (the meter)
    this.root.hidden = true;
    this.clearChart();
    this.geo = undefined;
  }

  private clearChart() {
    this.chartSeq++;
    this.destroyChart();
    this.destroyChart = () => {};
  }

  /** Open an area; `geo`: its /api/geo request when the caller has one going already (one request for both). */
  async show(geoId: string, geo?: Promise<GeoInfo>) {
    const seq = ++this.seq;
    this.clearChart();
    this.root.hidden = false;
    this.root.replaceChildren(el("div", { class: "panel-loading" }, "Loading…"));
    try {
      this.us ??= api.geo("us").catch(() => null);
      const [g, scores, us] = await Promise.all([geo ?? api.geo(geoId), api.scores(geoId), this.us]);
      if (seq !== this.seq) return;
      this.geo = g;
      this.scores = scores;
      this.render(us && g.level !== "us" ? us : null);
      performance.mark("panel:shown");
      if (this.d.gated) this.applyMeter(g);
    } catch (e) {
      if (seq !== this.seq) return;
      this.root.replaceChildren(el("div", { class: "panel-error" }, `Couldn't load this area. ${(e as Error).message}`),
        el("button", { class: "btn", onclick: () => this.d.onClose() }, "Close"));
    }
  }

  /** the open area (the sidebar's sign-up offers to follow it) */
  get area(): GeoInfo | undefined {
    return this.open ? this.geo : undefined;
  }

  /** whether gated parts of the panel ask for an account */
  private get locked() {
    return !!this.d.gated && account.gated;
  }

  /**
   * The meter (web/account.ts): the sign-up sheet at the gate's count ("Not now" once); after that, past twice the
   * count, the panel keeps "What this means" open and blurs the rest under a sign-up card.
   */
  private applyMeter(g: GeoInfo) {
    const m = meter(g.geo_id);
    if (m === "soft") openSignup({ source: "meter", geo: g.geo_id, geoName: areaTitle(g), dismissible: true, onDismiss: dismissMeter });
    else if (m === "hard") {
      const body = this.root.querySelector(".panel-body");
      if (!body) return;
      body.classList.add("gated");
      body.querySelector(".summary-card")?.after(el("section", { class: "card gate-card" },
        lockedTeaser(`Keep exploring ${g.name}, free`, "You've reached the areas you can open without an account. Sign up with your email to see every area's forecast, history and data: no password, no card.",
          { source: "meter", geo: g.geo_id, geoName: areaTitle(g) })));
    }
  }

  /** Where a data point's number includes a payment: which property-tax rule this area's uses (shared/regions.ts). */
  private taxNote(m: Metric, g: GeoInfo) {
    if (!usesPurchaseRule(m.id, fromOf((id) => this.d.metrics.find((x) => x.id === id)))) return null;
    return `In this area: ${taxRuleNote(inPurchaseRateState([g.geo_id, ...g.parents.map((p) => p.geo_id), ...(g.main_state ? [g.main_state] : [])]))}`;
  }

  /** Re-render the chart only (metric or freq changed). */
  async refreshChart() {
    if (!this.geo) return;
    const host = this.root.querySelector<HTMLElement>(".chart-host");
    if (!host) return;
    await this.drawChart(host);
    const sel = this.root.querySelector<HTMLSelectElement>(".metric-select");
    if (sel) sel.value = this.d.getMetric().id;
    const desc = this.root.querySelector(".metric-desc");
    if (desc) desc.textContent = this.d.getMetric().description;
    const tax = this.root.querySelector<HTMLElement>(".tax-note");
    if (tax) { const t = this.taxNote(this.d.getMetric(), this.geo); tax.textContent = t ?? ""; tax.hidden = !t; }
    this.root.querySelector(".latest")?.replaceWith(latestLine(this.d.getMetric(), this.geo.latest[this.d.getMetric().id], this.geo));
    this.renderChartControls();
  }

  /** A collapsed section that stays open or shut across re-renders. */
  private more(key: string, label: string, ...body: Array<Node | string | null | false>) {
    const d = el("details", { class: "more", open: this.openMore.has(key) }, el("summary", {}, label), ...nodes(...body));
    d.addEventListener("toggle", () => (d.open ? this.openMore.add(key) : this.openMore.delete(key)));
    return d;
  }

  private render(us: GeoInfo | null) {
    const g = this.geo!;
    const m = this.d.getMetric();
    // a breadcrumb, widest first: State › Metro › County (each opens that area)
    const crumbs = el("nav", { class: "crumbs", "aria-label": "Wider areas" },
      ...g.parents.flatMap((p, i) => [
        i ? el("span", { class: "crumb-sep", "aria-hidden": "true" }, "›") : null,
        el("button", { class: "crumb", title: `Open ${p.name}`, onclick: () => this.d.onNavigate(p.geo_id) }, p.name),
      ]).filter((n): n is HTMLElement => !!n));
    const badge = g.level === "zip" ? `ZIP ${g.code}` : g.level === "city" ? cityBadge(g) : LEVEL_LABEL[g.level]!;

    const header = el("header", { class: "panel-head" },
      el("div", { class: "head-left" },
        g.parents.length ? crumbs : null,
        el("div", { class: "panel-title" },
          el("h2", {}, g.name),
          el("span", { class: "level-badge", title: g.level === "city" ? cityNote(g) : "" }, badge))),
      el("div", { class: "panel-actions" },
        this.d.onCompare ? el("button", { class: "btn ghost small", title: "Compare with other areas", onclick: () => this.d.onCompare!(g.geo_id) }, "⇆ Compare") : null,
        this.locked
          ? el("button", { class: "btn ghost small", title: "Printable market report (free account)", onclick: () => openSignup({ source: "report", geo: g.geo_id, geoName: areaTitle(g), dismissible: true }) }, "⎙ Report")
          : el("a", { class: "btn ghost small", href: `#/report/${encodeURIComponent(g.geo_id)}`, target: "_blank", rel: "noopener", title: "Printable market report (save as PDF)" }, "⎙ Report"),
        // the area's market page: a link that previews with its own title and summary, and opens the map from there
        el("button", { class: "btn ghost small", title: "Copy a link to this area's market page",
          onclick: (e: Event) => {
            const b = e.currentTarget as HTMLElement;
            void navigator.clipboard?.writeText(`${location.origin}${marketPath(g)}`).then(() => { b.textContent = "✓ Copied"; setTimeout(() => (b.textContent = "⧉ Share"), 1500); });
          } }, "⧉ Share"),
        el("button", { class: "btn icon", "aria-label": "Close", onclick: () => this.d.onClose() }, "✕")));

    const scoreCard = el("section", { class: "card score-card" });
    this.renderScores(scoreCard);

    // a personal metric is listed once a budget is set (it then has months)
    const select = metricSelect({ metrics: this.d.metrics, value: m.id, onChange: (id) => this.d.setMetric(id),
      include: (x) => x.levels.includes(g.level) && (!x.personal || Object.keys(x.available).length > 0) });
    // the picker is the card's title; the latest value under it; one row of controls; the source at the foot
    const chartCard = el("section", { class: "card chart-card" },
      el("div", { class: "chart-head" }, el("div", { class: "metric-pick" }, select)),
      el("div", { class: "latest-row" }, latestLine(m, g.latest[m.id], g), el("div", { class: "latest-context muted small" })),
      el("p", { class: "metric-desc muted small" }, m.description),
      el("p", { class: "tax-note muted small", hidden: !this.taxNote(m, g) }, this.taxNote(m, g) ?? ""),
      el("div", { class: "chart-controls" }),
      el("div", { class: "chart-host" }),
      el("div", { class: "source-line muted small" }));

    this.root.replaceChildren(header, el("div", { class: "panel-body" }, summaryCard(g, this.scores!, us, (label, ...b) => this.more("summary", label, ...b), this.d.budget), followCard(g, !!this.d.gated), scoreCard, chartCard,
      caCard(g, this.d),
      g.latest.rent ? rentalCard(g.geo_id, (label, ...b) => this.more("rental-open", label, ...b), (label, ...b) => this.more("rental", label, ...b)) : null));
    this.renderChartControls();
    void this.drawChart(chartCard.querySelector(".chart-host")!);
  }

  /** The chart's control row (range, yearly / monthly, fair value, CSV) and its source line, for the current metric. */
  private renderChartControls() {
    const g = this.geo!, m = this.d.getMetric();
    const seg = <T extends string>(label: string, opts: Array<[T, string]>, cur: T, on: (v: T) => void) =>
      el("div", { class: "seg-group", role: "radiogroup", "aria-label": label },
        ...opts.map(([v, t]) => el("button", { class: `seg-btn ${v === cur ? "active" : ""}`, role: "radio", "aria-checked": String(v === cur), onclick: () => on(v) }, t)));
    this.root.querySelector(".chart-controls")?.replaceChildren(...nodes(
      seg<ChartRange>("Time range", [["5y", "5Y"], ["10y", "10Y"], ["all", "All"]], this.range, (v) => { this.range = v; void this.refreshChart(); }),
      // yearly and one-off data have one point a year at most: a Monthly choice would show the same chart
      m.cadence === "monthly" ? seg<"yearly" | "monthly">("Frequency", [["yearly", "Yearly"], ["monthly", "Monthly"]], this.d.getFreq(), (v) => this.d.setFreq(v)) : null,
      m.id === "home_value" ? el("button", { class: `toggle-chip ${this.showFair ? "on" : ""}`, "aria-pressed": String(this.showFair),
        title: "What this area's own pre-2020 price-to-income ratio implies at today's incomes. The gap to the line is Price vs. Incomes.",
        onclick: () => { this.showFair = !this.showFair; void this.refreshChart(); } }, "Fair value") : null,
      // raw Zillow / Realtor.com / Redfin series can't be redistributed (their terms): no download for those
      m.export === false ? null : el("button", { class: "btn ghost small csv-btn", title: "Download this history as CSV",
        onclick: () => (this.locked ? openSignup({ source: "csv", geo: g.geo_id, geoName: areaTitle(g), dismissible: true }) : void this.downloadSeries()) }, "↓ CSV")));
    this.root.querySelector(".source-line")?.replaceChildren(...nodes(
      `Source: ${m.source}${/Zillow/.test(m.source) ? " (Data Provided by Zillow Group)" : ""}`, el("span", { class: "sep" }, "·"),
      `Data: ${metricPeriod(m, g.latest[m.personal ? "mortgage_payment" : m.id]?.period)}`, el("span", { class: "sep" }, "·"),
      `Next update: ${nextUpdate(m)}`,
      ((o) => (o ? el("div", {}, zipOriginNote(g, o.partly)) : null))(zipOrigin(g, m.id))));
  }

  private renderScores(card: HTMLElement) {
    const s = this.scores!;
    const tabs: Array<[ScoreTab, string]> = [["forecast", "Price Outlook"], ["longterm", "Long-Run Demand"]];
    const body = el("div", { class: "score-body" });
    const draw = () => {
      body.replaceChildren();
      if (this.tab === "forecast") {
        const f = s.forecast;
        // how far its record here backs it (free: the record is the point), next to the answer, not in the workings
        const conf = forecastConfidence(s.track);
        // the answer first (label, what it means, the likely range); the gauge is part of the workings below
        body.append(...nodes(
          outlookPill(f.label, "big"),
          f.label ? el("p", { class: "forecast-reading" }, forecastReading(f.label, f.likely?.p_rise ?? null, conf)) : null,
          f.likely ? el("div", { class: "forecast-range" },
            rangeStrip(f.likely.lo, f.likely.mid, f.likely.hi),
            el("div", { class: "forecast-likely" }, "Likely next 12 months: ", el("strong", {}, `${pctChange(f.likely.lo)} to ${pctChange(f.likely.hi)}`),
              el("span", { class: "hint", title: "80% range around the model's forecast, from its past misses: how far the national model has missed since 2002 (including the 2007-11 bust) plus how far areas at this level land from their predicted position. A single area's year is hard to call; the range is the honest part." }, "i")),
            el("div", { class: "muted small" },
              `${riseLabel(conf)}: ${f.likely.p_rise == null ? "–" : `${Math.round(f.likely.p_rise * 100)}%`}`,
              f.likely.p_drop != null ? ` · of a 5%+ drop: ${Math.round(f.likely.p_drop * 100)}%` : "",
              // the forecast itself, the number the scorecard scores (the range sits around its calibrated center, v7.1)
              ` · forecast ${pctChange(f.likely.mid)}`,
              f.zillow ? ` · Zillow ${pctChange(f.zillow.value)}` : "")) : null,
          // the record is public (it's the point), right under the odds: direction right vs. the naive guess, the scorecard
          f.likely ? trackLine(this.geo!, conf) : null,
          s.best_months ? el("div", { class: "best-months" },
            el("div", {}, "Best month to buy: ", el("strong", {}, MONTHS_LONG[s.best_months.buy - 1]!)),
            el("div", {}, "Best month to sell: ", el("strong", {}, MONTHS_LONG[s.best_months.sell - 1]!)),
            s.best_months.source !== this.geo!.geo_id ? el("div", { class: "muted small" }, "Based on the surrounding area's seasonal pattern.") : null) : null,
          // the workings: drivers, why it moved, the rate what-if (a free account's, when gated)
          this.locked ? lockedTeaser("How we got this: drivers, rate what-if",
            "What's pushing this forecast up or down, why it moved since last month, and what a different mortgage rate would change.",
            { source: "forecast", geo: this.geo!.geo_id, geoName: areaTitle(this.geo!), dismissible: true }) :
          this.more("forecast", "How we got this: drivers, rate what-if",
            scoreMeter(f.score, "Price pressure",
              "0-100: each driver's score weighted by its share of this month's model. Above 50 = more upward price pressure than in a typical area this month. It ranks pressure; the likely range above is the forecast."),
            driverChart(f.inputs, f.score),
            forecastMoves(f.change),
            s.backtest ? el("a", { class: "muted small", href: "#/accuracy" },
              `Backtest at this level: rank correlation ${s.backtest.rho_model.toFixed(2)} vs. ${s.backtest.rho_naive.toFixed(2)} for “last year repeats”.`) : null,
            rateWhatIf(s.geo_id)),
        ));
      } else {
        body.append(...nodes(
          scoreMeter(s.lt_growth_score?.value ?? null, "Long-Run Demand Score", "Blend of 5-yr population and income growth, share aged 25-44, remote work and 10-yr appreciation, less new supply."),
          s.lt_growth_score ? el("div", { class: "muted small" }, `ACS ${s.lt_growth_score.period.slice(0, 4)} vintage`) : null));
      }
    };
    const tabBar = el("div", { class: "tabs", role: "tablist" },
      ...tabs.map(([k, label]) => el("button", {
        role: "tab", class: `tab ${k === this.tab ? "active" : ""}`, "aria-selected": k === this.tab ? "true" : "false",
        onclick: (e: Event) => {
          this.tab = k;
          for (const b of tabBar.querySelectorAll(".tab")) b.classList.toggle("active", b === e.currentTarget);
          draw();
        },
      }, label)));
    card.append(
      el("div", { class: "score-head" },
        el("h3", {}, `Forecast: ${s.forecast.period ? `${periodLabel(s.forecast.period)} → ${periodLabel(shift(s.forecast.period, 12))}` : "not available"}`)),
      tabBar, body);
    draw();
  }

  private async drawChart(host: HTMLElement) {
    this.clearChart();
    const seq = this.chartSeq;
    const m = this.d.getMetric();
    host.replaceChildren(el("div", { class: "muted small" }, "Loading history…"));
    try {
      // uPlot loads with the first chart (D5), alongside the series
      const [s, { historyChart }] = await Promise.all([api.series(this.geo!.geo_id, m.id, this.d.getFreq()), import("./chart.ts")]);
      if (seq !== this.chartSeq || !host.isConnected) return;
      // the time range: the last 5 / 10 years up to the newest point
      const last = s.points.at(-1)?.period;
      const years = this.range === "5y" ? 5 : this.range === "10y" ? 10 : 0;
      const from = last && years ? `${Number(last.slice(0, 4)) - years}${last.slice(4)}` : null;
      const shown = from ? { ...s, points: s.points.filter((p) => p.period >= from) } : s;
      this.destroyChart = historyChart(host, m, shown, { showFair: this.showFair });
      // a personal metric (from the user's budget) isn't among the area's stored latest values: take it from the series
      if (m.personal && s.context && !this.geo!.latest[m.id]) {
        this.geo!.latest[m.id] = { period: s.context.period, value: s.context.value };
        this.root.querySelector(".latest")?.replaceWith(latestLine(m, this.geo!.latest[m.id], this.geo!));
      }
      const ctx = this.root.querySelector(".latest-context");
      if (ctx) ctx.textContent = contextText(this.geo!.level, s.context);
    } catch (e) {
      if (seq !== this.chartSeq) return;
      host.textContent = `Couldn't load history (${(e as Error).message}).`;
    }
  }

  private async downloadSeries() {
    const m = this.d.getMetric();
    const geoId = this.geo!.geo_id; // taken now: the panel may close (geo cleared) while the series loads
    let s: Awaited<ReturnType<typeof api.series>>;
    try {
      s = await api.series(geoId, m.id, "monthly");
    } catch (e) {
      // like the history chart's: a short line next to the button
      this.root.querySelector(".csv-error")?.remove();
      this.root.querySelector(".csv-btn")?.after(el("span", { class: "csv-error small", role: "alert" }, `Couldn't download (${(e as Error).message}).`));
      return;
    }
    const csv = `period,${m.id}\n${s.points.map((p) => `${p.period},${p.value ?? ""}`).join("\n")}\n`;
    const a = el("a", { href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })), download: `${BRAND.slug}_${geoId.replace(":", "-")}_${m.id}.csv` });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }
}


const EMAIL_KEY = "monitusre.email.v1";
/**
 * "Follow" this area (its monthly update). Signed in: a toggle. Gated and not signed in: the sign-up sheet (following
 * is part of the account). Gate off: the original one-line sign-up, the email remembered in this browser.
 */
function followCard(g: GeoInfo, gated: boolean) {
  const text = el("div", { class: "follow-text" }, el("b", {}, `Follow ${g.name}`), el("span", { class: "muted small" }, " · one email a month: what changed in prices, supply and the outlook."));
  if (account.signedIn || (gated && account.gated)) {
    const btn = el("button", { class: "btn small primary" });
    const draw = () => {
      const on = account.follows(g.geo_id);
      btn.textContent = on ? "✓ Following" : "Follow";
      btn.classList.toggle("primary", !on);
      btn.title = on ? "Click to stop this area's monthly update" : "";
    };
    btn.addEventListener("click", async () => {
      if (!account.signedIn) return openSignup({ source: "follow", geo: g.geo_id, geoName: areaTitle(g), dismissible: true });
      btn.disabled = true;
      try { await account.follow(g.geo_id, !account.follows(g.geo_id)); } catch (e) { btn.title = (e as Error).message; }
      btn.disabled = false;
      draw();
    });
    draw();
    return el("section", { class: "card follow-card", "aria-label": "Monthly update" }, text, btn);
  }
  return legacyFollowCard(g, text);
}

function legacyFollowCard(g: GeoInfo, text: HTMLElement) {
  const remembered = (() => { try { return localStorage.getItem(EMAIL_KEY) ?? ""; } catch { return ""; } })();
  const input = el("input", { type: "email", required: true, placeholder: "you@example.com", value: remembered, "aria-label": "Email address", autocomplete: "email" });
  const status = el("span", { class: "muted small", role: "status" });
  const button = el("button", { class: "btn small primary", type: "submit" }, "Sign me up");
  const form = el("form", { class: "follow-form", onsubmit: async (e: Event) => {
    e.preventDefault();
    button.disabled = true;
    status.textContent = "";
    try {
      const r = await api.subscribe(input.value, g.geo_id);
      try { localStorage.setItem(EMAIL_KEY, input.value.trim()); } catch { /* private mode */ }
      form.replaceChildren(el("span", {}, `✓ ${r.message}`));
    } catch (err) {
      status.textContent = (err as Error).message;
      button.disabled = false;
    }
  } }, input, button, status);
  return el("section", { class: "card follow-card", "aria-label": "Monthly update" }, text, form);
}

/**
 * California's own layer (shared/catiles.ts) as tiles, for California areas only: fire and sea-level-rise zones, what's
 * being built, the housing target and SB 79. A tile opens its data point on the map; elsewhere no card and no note.
 */
function caCard(g: GeoInfo, d: PanelDeps) {
  const tiles = caTiles(g, (id) => d.metrics.find((m) => m.id === id));
  if (!tiles.length) return null;
  return el("section", { class: "card ca-card", "aria-label": CA_HEADING },
    el("h3", {}, CA_HEADING),
    el("div", { class: "ca-tiles" }, ...tiles.map((t) => el("button", { class: "ca-tile", title: "Show it on the map",
      onclick: () => (d.showCaMetric ?? d.setMetric)(t.id) },
      el("span", { class: "muted small" }, t.label), el("strong", {}, t.value), t.sub ? el("span", { class: "muted small" }, t.sub) : null))),
    el("p", { class: "muted small" }, CA_INSURANCE));
}

/**
 * "Rental underwriting": buy this area's typical home and rent it out. Starts from the area's numbers and the standard
 * investor assumptions (the same as the map's rental metrics); every input can be changed and the results follow.
 * Closed until asked for (2026-10-21 review): a landlord's cash flow, negative in most of the Bay Area, read as a verdict
 * on buying to live in. `open` keeps it open across re-renders; the numbers load on the first opening.
 */
function rentalCard(geoId: string, open: (label: string, ...body: Node[]) => HTMLElement, more: (label: string, ...body: Node[]) => HTMLElement) {
  const card = el("div", { class: "rental-body" });
  const details = open("Renting it out instead? Cash flow at today's rent", card);
  let loaded = false;
  const load = () => {
    if (loaded) return;
    loaded = true;
    void fill();
  };
  details.addEventListener("toggle", () => { if ((details as HTMLDetailsElement).open) load(); });
  const fill = () => api.underwrite(geoId).then((u) => {
    const d = u.defaults;
    const start: UnderwriteInput = {
      price: u.inputs.price, rent: u.inputs.rent, taxIns: u.inputs.tax_ins, rate: u.inputs.rate + d.ratePremium, termYears: d.termYears,
      down: d.down, closing: d.closing, vacancy: d.vacancy, management: d.management, maintenance: d.maintenance,
    };
    let x = { ...start };
    const usd = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? "–" : `${v < 0 ? "−" : ""}$${Math.round(Math.abs(v)).toLocaleString()}`);
    // [key, label, unit shown, to input, from input]
    const fields: Array<[keyof UnderwriteInput, string, string, (v: number) => string, (s: number) => number]> = [
      ["price", "Price", "$", (v) => String(Math.round(v)), (s) => s],
      ["rent", "Rent", "$/mo", (v) => String(Math.round(v)), (s) => s],
      ["taxIns", "Tax + insurance", "$/yr", (v) => String(Math.round(v)), (s) => s],
      ["rate", "Rate", "%", (v) => v.toFixed(2), (s) => s],
      ["down", "Down payment", "%", (v) => String(+(v * 100).toFixed(1)), (s) => s / 100],
      ["closing", "Closing costs", "%", (v) => String(+(v * 100).toFixed(1)), (s) => s / 100],
      ["vacancy", "Vacancy", "% of rent", (v) => String(+(v * 100).toFixed(1)), (s) => s / 100],
      ["management", "Management", "% of rent", (v) => String(+(v * 100).toFixed(1)), (s) => s / 100],
      ["maintenance", "Maintenance & capex", "% of rent", (v) => String(+(v * 100).toFixed(1)), (s) => s / 100],
    ];
    const out = el("div", { class: "rental-out" });
    const inputs = new Map<keyof UnderwriteInput, HTMLInputElement>();
    const render = () => {
      const r = underwrite(x);
      const tile = (label: string, value: string, tone = "", hint = "") =>
        el("div", { class: `rental-tile ${tone}`, title: hint }, el("div", { class: "muted small" }, label), el("strong", {}, value));
      out.replaceChildren(
        tile("Cash flow", `${usd(r.cashFlow)}/mo`, r.cashFlow >= 0 ? "up" : "down", "Rent after vacancy, management, maintenance, tax and insurance, minus the mortgage"),
        tile("Cash-on-cash", pct(r.cashOnCash), (r.cashOnCash ?? 0) >= 0 ? "up" : "down", "Yearly cash flow / cash put in"),
        tile("DSCR", r.dscr == null ? "–" : r.dscr.toFixed(2), (r.dscr ?? 0) >= 1.2 ? "up" : (r.dscr ?? 0) >= 1 ? "" : "down", "Net operating income / mortgage payments; lenders usually want about 1.2+"),
        tile("Cap rate", pct(r.capRate), "", "Net operating income / price (before the mortgage)"),
        tile("Break-even rent", `${usd(r.breakEvenRent)}/mo`, "", "The rent at which cash flow is zero"),
        tile("Cash needed", usd(r.cashIn), "", "Down payment + closing costs"));
    };
    const form = el("div", { class: "rental-form" }, ...fields.map(([k, label, unit, to, from]) => {
      const i = el("input", { type: "number", step: "any", value: to(x[k]), "aria-label": label, inputmode: "decimal" });
      inputs.set(k, i);
      i.addEventListener("input", () => { const v = Number(i.value); if (i.value !== "" && Number.isFinite(v)) { x = { ...x, [k]: from(v) }; render(); } });
      return el("label", { class: "budget-field" }, el("span", {}, `${label} (${unit})`), i);
    }));
    const reset = el("button", { class: "btn small", onclick: () => { x = { ...start }; for (const [k, , , to] of fields) inputs.get(k)!.value = to(x[k]); render(); } }, "Reset");
    const ratio = u.inputs.rent_ratio;
    card.replaceChildren(
      el("p", { class: "muted small" },
        `Buy the typical home and rent it out (${periodLabel(u.period)}). `,
        ratio && Math.abs(ratio - 1) > 0.005
          ? `Rent: Zillow's typical rent ${usd(u.inputs.rent_all)} × ${ratio.toFixed(2)}, the metro's single-family / all-homes rent ratio (houses rent for more than apartments). `
          : `Rent: Zillow's typical rent, all home types. `,
        `Rate: the month's average + ${d.ratePremium} pts for an investment loan. Change anything.`),
      out, more(`Edit assumptions (${fields.length})`, el("div", { class: "rental-edit" }, form, el("div", {}, reset))));
    render();
  }).catch(() => card.replaceChildren(el("p", { class: "muted small" }, "No rental numbers for this area.")));
  if ((details as HTMLDetailsElement).open) load();
  return el("section", { class: "card rental-card", "aria-label": "Rental underwriting" }, details);
}

/** "If the 30-year rate were 5.50% now": a slider that re-scores the forecast through the model's rate signal. */
function rateWhatIf(geoId: string) {
  const out = el("div", { class: "whatif-out small muted" });
  const label = el("span", { class: "whatif-rate" }, "");
  const slider = el("input", { type: "range", min: "3", max: "9", step: "0.25", "aria-label": "30-year mortgage rate, what-if" });
  const reset = el("button", { class: "link small", hidden: true }, "reset");
  const wrap = el("div", { class: "whatif" },
    el("div", { class: "small" }, el("strong", {}, "Rate what-if"), " · if the 30-year rate were ", label, " now ", reset,
      el("span", { class: "hint", title: "The model reads mortgage rates through one signal: how far the 30-year rate moved over the past 12 months, compared with the last 5 years. This re-scores the forecast with this month's rate set to the slider. Everything else stays as it is." }, "i")),
    slider, out);
  let actual: number | null = null, seq = 0, timer: ReturnType<typeof setTimeout> | undefined;
  const chance = (v: number | null | undefined) => (v == null ? "–" : `${Math.round(v * 100)}%`);
  const run = async (rateQ: number | null) => {
    const mine = ++seq;
    if (rateQ != null) label.textContent = `${rateQ.toFixed(2)}%`;
    try {
      const w = await api.whatIf(geoId, rateQ);
      if (mine !== seq) return;
      if (actual == null) { actual = w.rate.actual; slider.value = String(actual); label.textContent = `${actual.toFixed(2)}%`; }
      const rate = w.rate.what_if;
      reset.hidden = Math.abs(rate - (actual ?? rate)) < 0.01;
      const same = Math.abs(rate - w.rate.actual) < 0.01;
      out.replaceChildren(same
        ? `At this month's average (${w.rate.actual.toFixed(2)}%; a year earlier ${w.rate.year_ago.toFixed(2)}%): forecast ${pctChange(w.base.expected_change)}, chance of a rise ${chance(w.base.likely?.p_rise)}. Move the slider.`
        : el("span", {},
            "Forecast ", el("strong", {}, `${pctChange(w.base.expected_change)} → ${pctChange(w.what_if.expected_change)}`),
            ` · likely ${pctChange(w.what_if.likely?.lo)} to ${pctChange(w.what_if.likely?.hi)}`,
            ` · chance of a rise ${chance(w.base.likely?.p_rise)} → ${chance(w.what_if.likely?.p_rise)}`,
            ` · of a 5%+ drop ${chance(w.base.likely?.p_drop)} → ${chance(w.what_if.likely?.p_drop)}`,
            w.what_if.label !== w.base.label ? ` · now “${w.what_if.label}”` : ""));
    } catch (e) {
      if (mine === seq) out.textContent = `Couldn't run the what-if (${(e as Error).message}).`;
    }
  };
  slider.addEventListener("input", () => {
    const v = Number(slider.value);
    label.textContent = `${v.toFixed(2)}%`;
    clearTimeout(timer);
    timer = setTimeout(() => void run(v), 120);
  });
  reset.addEventListener("click", () => { if (actual != null) { slider.value = String(actual); void run(actual); } });
  // start at this month's actual rate (the API says what it was); no forecast here → no what-if
  void run(null).then(() => { if (actual == null) wrap.remove(); });
  return wrap;
}

/** "Since Jul 2026: −1.1% → −0.6% (+0.5 pts)" with the drivers that moved it. */
function forecastMoves(c: Scores["forecast"]["change"]) {
  const t = forecastChangeText(c);
  if (!t) return null;
  return el("div", { class: "forecast-moves" },
    el("div", { class: "small" }, el("strong", {}, "Why it moved"), " · ", t.headline,
      el("span", { class: "hint", title: "The forecast is a weighted sum of its signals. Each driver's part is how much its signals changed this month, at last month's weights; \"Model refit\" is the model learning one more month (its weights moving). Together they add up to the change." }, "i")),
    t.moves.length
      ? el("ul", {}, ...t.moves.map((m) => el("li", {},
          el("span", {}, m.label, m.bar ? el("span", { class: "muted" }, ` (bar ${m.bar})`) : null),
          el("span", { class: `move ${m.up ? "up" : "down"}` }, m.effect))))
      : el("div", { class: "muted small" }, "No driver moved it by more than 0.05 pts."));
}

/** Latest value under the chart title; an empty placeholder keeps its spot when the metric has no value here. */
function latestLine(m: Metric, latest: GeoInfo["latest"][string] | undefined, g: GeoInfo) {
  // a city's number built from its ZIPs says so where it's read, not only in the badge's tooltip
  const o = latest ? zipOrigin(g, m.id) : null;
  return el("div", { class: "latest" }, ...(latest ? [el("strong", {}, fmt(m, latest.value)), ` · ${metricPeriod(m, latest.period)}`,
    o ? el("span", { class: `origin-chip${g.zip_rough ? " rough" : ""}`, title: zipOriginNote(g, o.partly) }, o.label) : null] : []));
}

const TONE_TITLE: Record<Tone, string> = { buyer: "Favors buyers", seller: "Favors sellers", neutral: "Neutral" };

/** "What this means": the market verdict and one sentence per topic, from the area's latest numbers. */
function summaryCard(g: GeoInfo, s: Scores, us: GeoInfo | null, more: (label: string, ...body: Node[]) => HTMLElement, budget?: PanelDeps["budget"]) {
  const sum = summarize(g.latest, us?.latest ?? null, s.forecast, s.track);
  if (!sum.points.length) return null;
  const { lead, more: rest } = splitSummary(sum.points);
  const item = (p: (typeof sum.points)[number]) => el("li", {},
    el("i", { class: `dot ${p.tone}`, title: TONE_TITLE[p.tone] }),
    el("strong", {}, `${p.topic}. `), p.text,
    // the typical household's numbers; your own are one step away
    p.topic === "Affordability" && budget ? el("button", { class: "link summary-budget", onclick: () => (budget.has() ? budget.show() : budget.open()) },
      "See it for your income") : null);
  return el("section", { class: "card summary-card", "aria-label": "What this means" },
    el("div", { class: "summary-head" },
      el("h3", {}, "What this means"),
      sum.market ? el("span", { class: `verdict ${sum.market.tone}`, title: `From ${sum.market.signals} supply and negotiation signals (listings vs. 2019 and a year ago, sale-to-list, months of supply, price cuts and days on market vs. the U.S.)${sum.market.leverage == null ? "" : `. Buyer Leverage Score ${sum.market.leverage}/100: above ~65 buyers can negotiate, below ~35 sellers hold the cards.`}` },
        sum.market.label, sum.market.leverage == null ? null : el("span", { class: "verdict-score" }, ` · leverage ${sum.market.leverage}`)) : null,
      el("span", { class: "summary-key muted small" },
        el("i", { class: "dot buyer" }), "favors buyers", el("i", { class: "dot seller" }), "favors sellers")),
    el("ul", { class: "summary-points" }, ...lead.map(item)),
    rest.length ? more(`${rest.length} more: ${rest.map((p) => p.topic.toLowerCase()).join(", ")}`, el("ul", { class: "summary-points" }, ...rest.map(item))) : null);
}

function shift(period: string, months: number) {
  const [y, m] = period.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return d.toISOString().slice(0, 7);
}

/**
 * Rough next-release timing: a yearly data point's own release month (metrics.yaml `release_month:`, e.g. the CA DOJ's
 * crime file each July), else by source (Zillow mid-month, Realtor.com early month, ACS each December).
 */
export function nextUpdate(m: Pick<Metric, "source" | "cadence" | "release_month">, now = new Date()): string {
  const { source, cadence } = m;
  const mon = (d: Date) => d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const next = (day: number) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    if (now.getUTCDate() >= day) d.setUTCMonth(d.getUTCMonth() + 1);
    return d;
  };
  // out on the 1st of its month (the DOJ's 2025 file on 2026-07-01): from then on, next year's
  if (m.release_month) {
    const y = now.getUTCFullYear() + (now.getUTCMonth() + 1 >= m.release_month ? 1 : 0);
    return `${mon(new Date(Date.UTC(y, m.release_month - 1, 1)))} ${y}`;
  }
  if (/ACS|Census/.test(source) && cadence !== "monthly") return `Dec ${now.getUTCMonth() === 11 && now.getUTCDate() > 15 ? now.getUTCFullYear() + 1 : now.getUTCFullYear()}`;
  if (/Realtor/.test(source) && !/Zillow/.test(source)) return `Early-${mon(next(8))}`;
  if (cadence === "static") return "–";
  return `Mid-${mon(next(18))}`;
}

/**
 * The forecast's record here, free: direction right vs. "last year repeats" (a low-confidence warning where it doesn't
 * beat it), and a link to what it said vs. what happened: the area's public scorecard (California), else the accuracy page.
 */
function trackLine(g: GeoInfo, c: Confidence): HTMLElement {
  return el("p", { class: `forecast-track small ${c.kind}`,
    title: "The model refit each month on only the data known then, scored on the 12 months that followed. A reconstruction on today's revised data, not calls we made: the forecasts on record (archived since October 2026) start resolving with the August 2027 data." },
    confidenceText(areaTitle(g), c), " ",
    hasScorecard(g)
      ? el("a", { class: "record-link", href: scorecardPath(g) }, c.kind === "untested" ? "What it said vs. what happened →" : "See the record →")
      : el("a", { class: "record-link", href: "#/accuracy" }, "How accurate it has been →"));
}
